import type { UsageType, MonthlyBillingStatus } from '@/types/billing.types';
import i18n from '@/locales/i18n';

/**
 * Format an amount in centimes to euros with currency symbol
 */
export const formatAmount = (centimes: number): string => {
  const euros = centimes / 100;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(euros);
};

/**
 * Format a month and year to readable text
 */
export const formatMonthYear = (month: number, year: number): string => {
  const date = new Date(year, month - 1);
  return new Intl.DateTimeFormat('fr-FR', {
    month: 'long',
    year: 'numeric',
  }).format(date);
};

/**
 * Format a date to readable text
 */
export const formatBillingDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

/**
 * Get badge color based on billing status
 */
export const getBillingStatusColor = (status: MonthlyBillingStatus): string => {
  const colors: Record<MonthlyBillingStatus, string> = {
    draft: 'bg-neutral-100 text-neutral-800 border-neutral-200',
    pending: 'bg-warning-100 text-warning-800 border-warning-200',
    paid: 'bg-success-100 text-success-800 border-success-200',
    failed: 'bg-error-100 text-error-800 border-red-200',
    cancelled: 'bg-neutral-100 text-neutral-800 border-neutral-200',
  };
  return colors[status] || 'bg-neutral-100 text-neutral-800 border-neutral-200';
};

/**
 * Translate billing status using i18n
 */
export const translateBillingStatus = (
  status: MonthlyBillingStatus
): string => {
  return i18n.t(`billing.status.${status}`, status);
};

/**
 * Translate usage type using i18n
 */
export const translateUsageType = (type: UsageType): string => {
  return i18n.t(`billing.usageType.${type}`, type);
};

/**
 * Get icon name for usage type
 */
export const getUsageTypeIcon = (type: UsageType): string => {
  const icons: Record<UsageType, string> = {
    automation_executed: 'Zap',
    bulletin_sent: 'FileText',
    dsn_sent: 'Send',
    edition_generated: 'Download',
    edition_viewed: 'Eye',
  };
  return icons[type] || 'Circle';
};

/**
 * Calculate VAT amount from total
 */
export const calculateVAT = (totalHT: number, vatRate: number): number => {
  return Math.round(totalHT * (vatRate / 100));
};

/**
 * Calculate total TTC from HT
 */
export const calculateTotalTTC = (totalHT: number, vatRate: number): number => {
  return totalHT + calculateVAT(totalHT, vatRate);
};

/**
 * Get progress percentage for usage month
 */
export const getMonthProgress = (): number => {
  const now = new Date();
  const daysInMonth = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0
  ).getDate();
  const currentDay = now.getDate();
  return Math.round((currentDay / daysInMonth) * 100);
};

/**
 * Format number with spaces for thousands
 */
export const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('fr-FR').format(num);
};
